import { compact, isArray, map, reduce, reject, size } from "lodash";
import {
  useCurrentUserLocale,
  useCurrentUserTimezone,
} from "@linkfluence/core/user/use-cases";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import {
  useContextualWorkspaceAccounts,
  useContextualWorkspaceEntities,
} from "@radarly/core/context/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentWorkspace } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { Project } from "@radarly/models/project";
import { Workspace } from "@radarly/models/workspace";
import { IRouterService } from "@radarly/shared/router";
import { Page } from "@radarly/shared/router/pages";
import { Sitemap } from "@radarly/shared/router/sitemap";

function getFilteredPages(
  key: keyof typeof Sitemap,
  pages: Array<{ page: Page; pages: any }>,
  accounts: any,
  entities: any
) {
  if (key === "social") {
    const hasNoAccounts = !size(accounts);
    const hasNoEntities = !size(entities);
    return reject(pages, ({ page }) => {
      if (hasNoAccounts && page === Page.socialPerformanceOwnedOverview) {
        return true;
      } else if (
        hasNoEntities &&
        page === Page.socialPerformanceBenchmarkOverview
      ) {
        return true;
      } else {
        return false;
      }
    });
  } else {
    return pages;
  }
}

function isDisabled(page: Page) {
  return (
    page === Page.insightPagesPage ||
    page === Page.insightPagesEditor ||
    page === Page.insightPagesWizardEditor
  );
}

function getPages(
  sitemap: Partial<Sitemap>,
  router: IRouterService,
  project: Project,
  workspaceId: Workspace["id"],
  accounts: any,
  entities: any,
  locale: Locale,
  timezone: Timezone
) {
  return project
    ? compact(
        map(sitemap, ({ page, pages }, id: keyof typeof Sitemap) => {
          const label = tr(locale, "HEADER::" + id + "::TITLE");
          if (isArray(pages)) {
            const filteredPages = getFilteredPages(
              id,
              pages,
              accounts,
              entities
            );
            if (size(filteredPages)) {
              const hasOnlyDisabledPages = !size(
                reject(filteredPages, (subpage) => isDisabled(subpage.page))
              );
              return {
                id,
                label,
                url: hasOnlyDisabledPages
                  ? router.getWorkspaceUrlFromPage(page, project, workspaceId, {
                      timezone,
                    })
                  : "",
                pages: map(filteredPages, (subpage) => ({
                  id: subpage.page,
                  label: hasOnlyDisabledPages
                    ? ""
                    : tr(locale, "HEADER::" + subpage.page),
                  disabled: isDisabled(subpage.page),
                  url: router.getWorkspaceUrlFromPage(
                    subpage.page,
                    project,
                    workspaceId,
                    { timezone }
                  ),
                })),
              };
            } else {
              return null;
            }
          } else {
            return {
              id,
              label,
              url: router.getWorkspaceUrlFromPage(page, project, workspaceId, {
                timezone,
              }),
            };
          }
        })
      )
    : [];
}

const reduceSiteMap = (sitemap: Sitemap, workspace: Workspace) => {
  return reduce(
    sitemap,
    (acc, pages, key) => {
      if (key === Page.discovery) {
        if (workspace.isDiscoveryActivated) {
          acc[key] = pages;
        }
      } else if (key === Page.listening) {
        if (!workspace.hasValidEmotionalMap) {
          pages.pages = reject(pages.pages, {
            page: Page.listeningContentLandscape,
          });
        }
        acc[key] = pages;
      } else {
        acc[key] = pages;
      }
      return acc;
    },
    {}
  );
};

export function useNavbarPages(router: IRouterService) {
  const project = useCurrentProject();
  const workspace = useCurrentWorkspace();
  const accounts = useContextualWorkspaceAccounts();
  const entities = useContextualWorkspaceEntities();
  const locale = useCurrentUserLocale();
  const timezone = useCurrentUserTimezone();
  return {
    pages: getPages(
      reduceSiteMap(Sitemap, workspace),
      router,
      project,
      workspace.id,
      accounts,
      entities,
      locale,
      timezone
    ),
    limit: 3,
    locale,
  };
}
